import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getSpeechStatus, transcribeSpeechAudio } from '../api';
import {
  getCitasConsultorio,
  getFacturasConsultorio,
  getPacientesConsultorio,
  getProductosConsultorio,
} from '../data/consultorioData';
import { useConsultorioCitasOptional } from '../context/ConsultorioCitasContext';
import { useAuth } from '../context/AuthContext';
import { useConsultorioEquipo } from './useConsultorioEquipo';
import { parseConsultorioVoiceIntent, HELP_TEXT } from '../utils/consultorioVoiceIntents';
import { formatApiError } from '../utils/apiErrorMessage';
import { extensionForMime, startVoiceRecording } from '../utils/voiceRecorder';

/**
 * Asistente de voz global del consultorio (FAB flotante).
 */
export function useConsultorioVoiceAssistant() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { equipo, equipoIds } = useConsultorioEquipo(user);
  const citasCtx = useConsultorioCitasOptional();

  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [backendReady, setBackendReady] = useState(false);
  const [provider, setProvider] = useState(null);
  const [awaitingConfirm, setAwaitingConfirm] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState('');

  const [pacientes, setPacientes] = useState([]);
  const [citas, setCitas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [facturas, setFacturas] = useState([]);

  const recorderRef = useRef(null);
  const pressSeqRef = useRef(0);
  const busyRef = useRef(false);
  const awaitingConfirmRef = useRef(false);

  const reloadData = useCallback(async () => {
    if (!user?.id) return;
    const ids = equipoIds.length ? equipoIds : [user.id];
    const [p, c, prod, fac] = await Promise.all([
      getPacientesConsultorio(ids),
      getCitasConsultorio(ids),
      getProductosConsultorio(user.id),
      getFacturasConsultorio(user.id),
    ]);
    setPacientes(Array.isArray(p) ? p : []);
    setCitas(Array.isArray(c) ? c : []);
    setProductos(Array.isArray(prod) ? prod : []);
    setFacturas(Array.isArray(fac) ? fac : []);
  }, [user?.id, equipoIds]);

  useEffect(() => {
    if (open) reloadData();
  }, [open, reloadData]);

  useEffect(() => {
    let cancelled = false;
    getSpeechStatus()
      .then(({ data }) => {
        if (cancelled) return;
        setBackendReady(!!data?.available);
        setProvider(data?.provider || null);
        setFeedback(
          data?.available
            ? HELP_TEXT
            : (data?.hint || 'Whisper no está disponible en el servidor.'),
        );
      })
      .catch((err) => {
        if (!cancelled) {
          setBackendReady(false);
          setFeedback(formatApiError(err, 'No se pudo conectar al motor de voz.'));
        }
      });
    return () => { cancelled = true; };
  }, []);

  const runIntent = useCallback(async (text) => {
    const modalOpen = !!citasCtx?.showCitaModal;
    const intent = parseConsultorioVoiceIntent(text, {
      pacientes,
      citas,
      productos,
      facturas,
      equipo,
      awaitingConfirm: awaitingConfirmRef.current,
      modalOpen,
    });
    if (!intent) return;
    if (intent.feedback) setFeedback(intent.feedback);

    if (intent.clearConfirm) {
      awaitingConfirmRef.current = false;
      setAwaitingConfirm(false);
      setConfirmSummary('');
    }

    if (intent.type === 'NAVIGATE' || intent.type === 'SPEAK_NAV') {
      if (intent.path) navigate(intent.path);
      return;
    }
    if (intent.type === 'HELP' || intent.type === 'STATUS') return;

    if (intent.type === 'OPEN_CITA' || intent.type === 'OPEN_MODAL') {
      citasCtx?.openNuevaCitaModal?.({ formPatch: intent.patch || {} });
      return;
    }

    if (intent.type === 'SCHEDULE_DRAFT') {
      if (modalOpen) citasCtx?.applyCitaFormPatch?.(intent.patch || {});
      else citasCtx?.openNuevaCitaModal?.({ formPatch: intent.patch || {} });
      if (intent.needsConfirm) {
        awaitingConfirmRef.current = true;
        setAwaitingConfirm(true);
        setConfirmSummary(intent.summary || '');
      } else {
        awaitingConfirmRef.current = false;
        setAwaitingConfirm(false);
        setConfirmSummary('');
      }
      return;
    }

    if (intent.type === 'SAVE') {
      await citasCtx?.saveCitaForm?.();
      awaitingConfirmRef.current = false;
      setAwaitingConfirm(false);
      setConfirmSummary('');
      await reloadData();
      return;
    }

    if (intent.type === 'CLOSE_MODAL') {
      citasCtx?.setShowCitaModal?.(false);
      awaitingConfirmRef.current = false;
      setAwaitingConfirm(false);
      setConfirmSummary('');
      return;
    }

    if (intent.type === 'PATCH' && intent.patch) {
      citasCtx?.applyCitaFormPatch?.(intent.patch);
      return;
    }

    if (intent.type === 'TIME_RANGE' && intent.start) {
      const today = new Date().toISOString().slice(0, 10);
      citasCtx?.applyCitaFormPatch?.({
        fechaHora: `${today}T${intent.start}`,
        fechaHoraFin: `${today}T${intent.end || intent.start}`,
        _voiceMergeDay: true,
      });
      return;
    }

    if (intent.type === 'TIME_START' && intent.time) {
      const today = new Date().toISOString().slice(0, 10);
      citasCtx?.applyCitaFormPatch?.({
        fechaHora: `${today}T${intent.time}`,
        _voiceMergeDay: true,
      });
    }
  }, [citasCtx, pacientes, citas, productos, facturas, equipo, navigate, reloadData]);

  const pressTalk = useCallback(async () => {
    if (!open || busyRef.current || listening || transcribing) return;
    if (!backendReady) {
      setFeedback('Configurá Whisper en el microservicio e instalá faster-whisper.');
      return;
    }
    const seq = pressSeqRef.current + 1;
    pressSeqRef.current = seq;
    busyRef.current = true;
    try {
      const rec = await startVoiceRecording({ mode: 'hold', maxMs: 30000 });
      if (pressSeqRef.current !== seq) {
        rec.cancel();
        busyRef.current = false;
        return;
      }
      recorderRef.current = rec;
      setListening(true);
      setLiveTranscript('');
      setFeedback('Hablá… soltá para procesar.');
    } catch (err) {
      setListening(false);
      setFeedback(
        /Permission|NotAllowed|denied/i.test(String(err?.message || err))
          ? 'Permití el micrófono en el navegador.'
          : (err?.message || 'No se pudo grabar.'),
      );
    } finally {
      busyRef.current = false;
    }
  }, [open, listening, transcribing, backendReady]);

  const releaseTalk = useCallback(async () => {
    pressSeqRef.current += 1;
    const rec = recorderRef.current;
    if (!rec) {
      setListening(false);
      return;
    }
    recorderRef.current = null;
    setListening(false);
    setTranscribing(true);
    setFeedback('Procesando…');
    try {
      const blob = await rec.stop();
      if (!blob || blob.size < 250) {
        setFeedback('No escuché nada. Mantené el micrófono y hablá.');
        return;
      }
      const ext = extensionForMime(blob.type);
      const { data } = await transcribeSpeechAudio(blob, `comando.${ext}`);
      const text = (data?.text || '').trim();
      setLiveTranscript(text);
      if (!text) setFeedback('No entendí. Probá de nuevo.');
      else await runIntent(text);
    } catch (err) {
      setFeedback(formatApiError(err, 'No se pudo transcribir.'));
    } finally {
      setTranscribing(false);
    }
  }, [runIntent]);

  const confirmPending = useCallback(() => {
    if (!awaitingConfirmRef.current) return;
    runIntent('sí');
  }, [runIntent]);

  const rejectPending = useCallback(() => {
    if (!awaitingConfirmRef.current) return;
    runIntent('no');
  }, [runIntent]);

  return {
    open,
    setOpen,
    listening,
    transcribing,
    feedback,
    liveTranscript,
    backendReady,
    provider,
    awaitingConfirm,
    confirmSummary,
    pressTalk,
    releaseTalk,
    confirmPending,
    rejectPending,
    runIntent,
    helpText: HELP_TEXT,
  };
}
